import { 
  CompleteKundali, 
  KundaliChartData, 
  PlanetPosition, 
  PlanetName, 
  ZodiacSign 
} from '../types/kundali';
import { getAspectsOnHouse } from './astrologyRules';
import { ZODIAC_LORDS, ZODIAC_SIGNS, getSavByBhava } from './vedicEngine';
import { formatOrdinal } from './dateUtils';

export type BhavaVitalityRating = 'Exemplary (Purna)' | 'Strong (Bala)' | 'Moderate (Madhyama)' | 'Afflicted (Peedita)' | 'Mixed (Mishra)';

export type SaptaVargajaDignity = 
  | 'Exalted / Uccha (60/60)'
  | 'Moola-Trikona (45/60)'
  | 'Own Sign / Swa-Kshetra (30/60)'
  | 'Great Friend / Adhi-Mitra (22.5/60)'
  | 'Friend / Mitra (15/60)'
  | 'Neutral / Sama (7.5/60)'
  | 'Enemy / Shatru (3.75/60)'
  | 'Great Enemy / Adhi-Shatru (1.875/60)'
  | 'Debilitated / Neecha (0/60)';

export type KartariFormation = 'Shubha Kartari (Flanked by Benefics)' | 'Papa Kartari (Hemmed by Malefics)' | 'Neutral / Open';

export interface PlanarAspectVirupas {
  planet: string;
  isBenefic: boolean;
  aspectType: string;
  fromHouse: number;
  drishtiStrengthVirupas: number; // 0 to 60 virupas
}

export interface BhavaVesselCondition {
  houseNumber: number;
  sign: ZodiacSign;
  signSanskrit: string;
  houseCategory: 'Kendra' | 'Trikona' | 'Upachaya' | 'Dusthana' | 'Maraka' | 'Neutral';
  occupants: Array<{
    planet: PlanetName;
    dignity: SaptaVargajaDignity;
    isFunctionalBenefic: boolean;
    isYogakaraka: boolean;
    combustionStatus: boolean;
    isRetrograde: boolean;
    degreeFormatted: string;
    nakshatra: string;
  }>;
  inboundDrishti: PlanarAspectVirupas[];
  kartariStatus: KartariFormation;
  flankingPlanets: { preceding12th: string[]; succeeding2nd: string[] };
  netVesselScore: number; // 0 - 100
}

export interface BhaveshaManagerCondition {
  lordPlanet: PlanetName;
  placedInHouse: number;
  placedInSign: ZodiacSign;
  houseFromOwnSign: number;
  saptaVargaDignity: SaptaVargajaDignity;
  dignityScorePoints: number; // 0 to 60
  dispositor: {
    planet: PlanetName;
    sign: ZodiacSign;
    house: number;
    dignity: SaptaVargajaDignity;
    isStrong: boolean;
  };
  combustion: { isCombust: boolean; orbDegrees?: number };
  retrogradation: { isVakri: boolean };
  functionalRole: string;
  netLordScore: number; // 0 - 100
}

export interface KarakaDeliverableCondition {
  naisargikaKaraka: PlanetName;
  placedInHouse: number;
  placedInSign: ZodiacSign;
  karakaDignity: SaptaVargajaDignity;
  karakoBhavanashaya: boolean;
  karakoBhavanashayaNote?: string;
  karakaVitalityScore: number; // 0 - 100
}

export interface BhavatBhavamEvaluation {
  reflectedHouseNumber: number;
  reflectedHouseSign: ZodiacSign;
  reflectedLord: PlanetName;
  reflectedLordHouse: number;
  reflectedLordDignity: SaptaVargajaDignity;
  reflectedHouseStrength: 'Affirmative & Fortifying' | 'Neutral & Steady' | 'Compromised / Under Strain';
  synthesisNote: string;
}

export interface ArgalaIntervention {
  houseNumber: number;
  argalaType: 'Primary Dhanargala (2nd)' | 'Sukha Argala (4th)' | 'Labhargala (11th)' | 'Secondary Sutargala (5th)';
  planetsCausing: string[];
  isBeneficArgala: boolean;
  opposingVirodhaHouse: number;
  obstructingPlanets: string[];
  isArgalaObstructed: boolean;
  netInterventionVerdict: 'Unobstructed Benefic Support' | 'Obstructed Benefic Support' | 'Unobstructed Malefic Pressure' | 'Obstructed Malefic Tension' | 'No Intervention';
}

export interface VargaNavamshaBacking {
  d9NavamshaSign: ZodiacSign;
  d9NavamshaLord: PlanetName;
  d9Dignity: string;
  isPushkarNavamsha: boolean;
  isPushkarBhaga: boolean;
  isVargottama: boolean;
  ashtakavargaBAVPoints: number;
  bavEvaluation: 'Abundant (≥30 Bindus)' | 'Average (28-29 Bindus)' | 'Deficient (<28 Bindus)';
}

export interface CompleteBhavaConditionAssessment {
  meta: {
    houseNumber: number;
    sanskritName: string;
    englishTitle: string;
    coreSignifications: string[];
  };
  headerSummary: {
    netVitality: BhavaVitalityRating;
    compositeScorePct: number;
    bavBinduCount: number;
    houseArchetype: string;
    executiveSummary: string;
  };
  triPillar: {
    vessel: BhavaVesselCondition;
    manager: BhaveshaManagerCondition;
    deliverable: KarakaDeliverableCondition;
  };
  advancedVedicMetrics: {
    bhavatBhavam: BhavatBhavamEvaluation;
    argalaMatrix: ArgalaIntervention[];
    vargaConfirmations: VargaNavamshaBacking;
  };
  synthesisAndActivation: {
    manifestationPromise: {
      coreStrengths: string[];
      latentBottlenecks: string[];
    };
    activationTriggers: {
      activeDashaCorrelation: string;
      favorableTransits: string[];
      vulnerableTransits: string[];
    };
    classicalRemedies: string[];
  };
}

const SIGN_SANSKRIT: Record<ZodiacSign, string> = {
  Aries: 'Mesha',
  Taurus: 'Vrishabha',
  Gemini: 'Mithuna',
  Cancer: 'Karka',
  Leo: 'Simha',
  Virgo: 'Kanya',
  Libra: 'Tula',
  Scorpio: 'Vrishchika',
  Sagittarius: 'Dhanu',
  Capricorn: 'Makara',
  Aquarius: 'Kumbha',
  Pisces: 'Meena'
};

const NATURAL_BENEFICS: PlanetName[] = ['Jupiter', 'Venus', 'Moon', 'Mercury'];
const NATURAL_MALEFICS: PlanetName[] = ['Saturn', 'Mars', 'Sun', 'Rahu', 'Ketu'];

const PRIMARY_NAISARGIKA_KARAKAS: Record<number, PlanetName> = {
  1: 'Sun',      // Tanu Bhava (Self, Vitality)
  2: 'Jupiter',  // Dhana & Kutumba (Wealth, Speech, Family)
  3: 'Mars',     // Sahaja (Courage, Younger Siblings)
  4: 'Moon',     // Sukha & Matru (Mother, Emotional Peace, Vehicles)
  5: 'Jupiter',  // Putra & Buddhi (Progeny, Intelligence, Mantras)
  6: 'Mars',     // Shatru & Rina (Enemies, Debts, Litigation; Saturn also for disease)
  7: 'Venus',    // Kalatra (Spouse, Marriage, Partnership)
  8: 'Saturn',   // Ayur & Randhra (Longevity, Transformation, Chronic matters)
  9: 'Jupiter',  // Dharma & Bhagya (Guru, Higher Learning, Fortune)
  10: 'Sun',     // Karma & Rajya (Career, Authority, Recognition; Mercury/Saturn secondary)
  11: 'Jupiter', // Labha & Aya (Gains, Aspirations, Elder Siblings)
  12: 'Saturn'   // Vyaya & Moksha (Losses, Foreign Lands, Liberation; Ketu also for Moksha)
};

/**
 * Check if a degree within a sign is in Pushkar Navamsha
 */
export function isPushkarNavamsha(sign: ZodiacSign, degreeInSign: number): boolean {
  const fireSigns: ZodiacSign[] = ['Aries', 'Leo', 'Sagittarius'];
  const earthSigns: ZodiacSign[] = ['Taurus', 'Virgo', 'Capricorn'];
  const airSigns: ZodiacSign[] = ['Gemini', 'Libra', 'Aquarius'];
  const waterSigns: ZodiacSign[] = ['Cancer', 'Scorpio', 'Pisces'];

  // 9 Navamshas of 3°20' (3.3333°)
  const navamshaIndex = Math.floor(degreeInSign / 3.333333333333); // 0 to 8

  if (fireSigns.includes(sign)) {
    // 7th (index 6, Libra) and 9th (index 8, Sagittarius)
    return navamshaIndex === 6 || navamshaIndex === 8;
  }
  if (earthSigns.includes(sign)) {
    // 3rd (index 2, Pisces) and 5th (index 4, Taurus)
    return navamshaIndex === 2 || navamshaIndex === 4;
  }
  if (airSigns.includes(sign)) {
    // 6th (index 5, Pisces) and 8th (index 7, Taurus)
    return navamshaIndex === 5 || navamshaIndex === 7;
  }
  if (waterSigns.includes(sign)) {
    // 1st (index 0, Cancer) and 3rd (index 2, Virgo)
    return navamshaIndex === 0 || navamshaIndex === 2;
  }
  return false;
}

/**
 * Check if a degree is Pushkar Bhaga (exact auspicious degree)
 */
export function isPushkarBhaga(sign: ZodiacSign, degreeInSign: number): boolean {
  const pushkarDegrees: Record<ZodiacSign, number[]> = {
    Aries: [21],
    Taurus: [14],
    Gemini: [18, 24],
    Cancer: [8, 19],
    Leo: [19],
    Virgo: [9],
    Libra: [24],
    Scorpio: [11],
    Sagittarius: [9],
    Capricorn: [14],
    Aquarius: [19],
    Pisces: [9]
  };

  const targets = pushkarDegrees[sign] || [];
  return targets.some(deg => Math.abs(degreeInSign - deg) <= 1.0);
}

/**
 * Map raw dignity to Sapta-Vargaja 9-tier scale
 */
export function mapToSaptaVargaja(dignity: string, isRetrograde: boolean = false): { dignity: SaptaVargajaDignity; points: number } {
  switch (dignity) {
    case 'Exalted':
      return { dignity: 'Exalted / Uccha (60/60)', points: 60 };
    case 'Moolatrikona':
      return { dignity: 'Moola-Trikona (45/60)', points: 45 };
    case 'Own House':
      return { dignity: 'Own Sign / Swa-Kshetra (30/60)', points: 30 };
    case 'Great Friend':
      return { dignity: 'Great Friend / Adhi-Mitra (22.5/60)', points: 22.5 };
    case 'Friend':
      return { dignity: 'Friend / Mitra (15/60)', points: 15 };
    case 'Neutral':
      return { dignity: 'Neutral / Sama (7.5/60)', points: 7.5 };
    case 'Enemy':
      return { dignity: 'Enemy / Shatru (3.75/60)', points: 3.75 };
    case 'Great Enemy':
      return { dignity: 'Great Enemy / Adhi-Shatru (1.875/60)', points: 1.875 };
    case 'Debilitated':
      return { dignity: 'Debilitated / Neecha (0/60)', points: 0 };
    default:
      return { dignity: 'Neutral / Sama (7.5/60)', points: 7.5 };
  }
}

/**
 * Evaluate complete Parashari condition of any Bhava (House 1 to 12)
 */
export function evaluateCompleteBhavaCondition(
  houseNumber: number,
  chartData: KundaliChartData,
  kundali: CompleteKundali
): CompleteBhavaConditionAssessment {
  const safeChartData = chartData || kundali?.charts?.D1;
  const houses = safeChartData?.houses || kundali?.houses || [];
  const house = houses.find(h => h.houseNumber === houseNumber) || houses[0] || {
    houseNumber,
    sign: 'Aries',
    signLord: 'Mars',
    degreeFormatted: '00°00\'00"',
    nakshatra: 'Ashwini',
    pada: 1,
    planets: []
  };
  const houseSign = house.sign || 'Aries';
  const houseSignSanskrit = SIGN_SANSKRIT[houseSign] || houseSign;
  const ascSignIndex = safeChartData?.ascendantIndex ?? 0;

  // 1. Vessel (The House) Occupants & Inbound Drishti
  const chartPlanets = safeChartData?.planets || kundali?.planets || [];
  const rawOccupants = chartPlanets.filter(p => p.house === houseNumber && p.name !== 'Ascendant');
  const occupants = rawOccupants.map(p => {
    const sv = mapToSaptaVargaja(p.dignity, p.isRetrograde);
    const isBenefic = NATURAL_BENEFICS.includes(p.name);
    return {
      planet: p.name,
      dignity: sv.dignity,
      isFunctionalBenefic: isBenefic,
      isYogakaraka: false,
      combustionStatus: p.isCombust,
      isRetrograde: p.isRetrograde,
      degreeFormatted: p.degreeFormatted,
      nakshatra: p.nakshatra
    };
  });

  // Aspects Virupas
  const aspectsRaw = getAspectsOnHouse(houseNumber, chartData.planets, ascSignIndex);
  const inboundDrishti: PlanarAspectVirupas[] = aspectsRaw.map(a => {
    let virupas = 45;
    if (a.aspectType.includes('Full') || a.aspectType.includes('Direct')) virupas = 60;
    if (a.aspectType.includes('Special')) virupas = 50;
    return {
      planet: a.planet,
      isBenefic: a.isBenefic,
      aspectType: a.aspectType,
      fromHouse: a.sourceHouse,
      drishtiStrengthVirupas: virupas
    };
  });

  // Kartari Status (12th and 2nd from house)
  const precedingHouseNum = houseNumber === 1 ? 12 : houseNumber - 1;
  const succeedingHouseNum = houseNumber === 12 ? 1 : houseNumber + 1;

  const precPlanets = chartData.planets.filter(p => p.house === precedingHouseNum && p.name !== 'Ascendant').map(p => p.name);
  const succPlanets = chartData.planets.filter(p => p.house === succeedingHouseNum && p.name !== 'Ascendant').map(p => p.name);

  const precHasBenefic = precPlanets.some(p => NATURAL_BENEFICS.includes(p));
  const precHasMalefic = precPlanets.some(p => NATURAL_MALEFICS.includes(p));
  const succHasBenefic = succPlanets.some(p => NATURAL_BENEFICS.includes(p));
  const succHasMalefic = succPlanets.some(p => NATURAL_MALEFICS.includes(p));

  let kartariStatus: KartariFormation = 'Neutral / Open';
  if (precHasBenefic && succHasBenefic && !precHasMalefic && !succHasMalefic) {
    kartariStatus = 'Shubha Kartari (Flanked by Benefics)';
  } else if (precHasMalefic && succHasMalefic && !precHasBenefic && !succHasBenefic) {
    kartariStatus = 'Papa Kartari (Hemmed by Malefics)';
  }

  // Vessel Net Score (0 - 100)
  let vesselScore = 50;
  if ([1, 4, 7, 10].includes(houseNumber)) vesselScore += 10; // Kendra
  if ([5, 9].includes(houseNumber)) vesselScore += 15; // Trikona
  if ([6, 8, 12].includes(houseNumber)) vesselScore -= 10; // Dusthana

  occupants.forEach(occ => {
    if (NATURAL_BENEFICS.includes(occ.planet)) vesselScore += 12;
    if (NATURAL_MALEFICS.includes(occ.planet)) {
      if ([3, 6, 11].includes(houseNumber)) vesselScore += 8; // Upachaya malefic boost
      else vesselScore -= 10;
    }
  });

  inboundDrishti.forEach(asp => {
    if (asp.isBenefic) vesselScore += 8;
    else vesselScore -= 6;
  });

  if (kartariStatus === 'Shubha Kartari (Flanked by Benefics)') vesselScore += 15;
  if (kartariStatus === 'Papa Kartari (Hemmed by Malefics)') vesselScore -= 18;

  vesselScore = Math.max(10, Math.min(98, vesselScore));

  // 2. Bhavesha (The Resource Manager / House Lord)
  const lordPlanet = house.signLord as PlanetName;
  const lordPos = chartData.planets.find(p => p.name === lordPlanet);
  const lordPlacedHouse = lordPos?.house || 1;
  const lordPlacedSign = lordPos?.sign || 'Aries';

  const lordSv = mapToSaptaVargaja(lordPos?.dignity || 'Neutral', lordPos?.isRetrograde || false);
  const houseFromOwn = ((lordPlacedHouse - houseNumber + 12) % 12) + 1;

  // Dispositor of the Lord
  const dispositorLordName = ZODIAC_LORDS[lordPlacedSign] as PlanetName;
  const dispositorPos = chartData.planets.find(p => p.name === dispositorLordName);
  const dispositorSv = mapToSaptaVargaja(dispositorPos?.dignity || 'Neutral', dispositorPos?.isRetrograde || false);
  const isDispositorStrong = dispositorSv.points >= 22.5 && ![6, 8, 12].includes(dispositorPos?.house || 1);

  let lordScore = 30 + (lordSv.points * 0.7);
  if ([1, 4, 7, 10, 5, 9, 11].includes(lordPlacedHouse)) lordScore += 15;
  if ([6, 8, 12].includes(lordPlacedHouse) && ![6, 8, 12].includes(houseNumber)) lordScore -= 20;
  if (lordPos?.isCombust) lordScore -= 15;
  if (lordPos?.isRetrograde && lordPos.dignity !== 'Debilitated') lordScore += 10;
  if (isDispositorStrong) lordScore += 10;

  lordScore = Math.max(10, Math.min(99, Math.round(lordScore)));

  // 3. Karaka (The Deliverable)
  const karakaPlanet = PRIMARY_NAISARGIKA_KARAKAS[houseNumber] || 'Jupiter';
  const karakaPos = chartData.planets.find(p => p.name === karakaPlanet);
  const karakaSv = mapToSaptaVargaja(karakaPos?.dignity || 'Neutral', karakaPos?.isRetrograde || false);
  
  // Check Karako Bhavo Nashaya (Karaka alone in own bhava)
  let karakoBhavanashaya = false;
  let karakoBhavanashayaNote: string | undefined;

  if (karakaPos && karakaPos.house === houseNumber) {
    const otherOccupants = occupants.filter(o => o.planet !== karakaPlanet);
    if (otherOccupants.length === 0) {
      if (['Jupiter', 'Venus', 'Sun', 'Mars'].includes(karakaPlanet)) {
        karakoBhavanashaya = true;
        karakoBhavanashayaNote = `Classical Parashari Karako Bhavo Nashaya: Natural significator ${karakaPlanet} placed isolated in House ${houseNumber} can create excessive attachment or delay in full fruit realization unless aspected by friendly benefics.`;
      }
    }
  }

  let karakaScore = 40 + (karakaSv.points * 0.6);
  if ([1, 4, 7, 10, 5, 9, 11].includes(karakaPos?.house || 1)) karakaScore += 12;
  if ([6, 8, 12].includes(karakaPos?.house || 1)) karakaScore -= 15;
  if (karakoBhavanashaya) karakaScore -= 12;

  karakaScore = Math.max(10, Math.min(98, Math.round(karakaScore)));

  // 4. Bhavat Bhavam (Secondary Reflected House)
  // Formula: (2N - 1) - 1 % 12 + 1 = (2 * houseNumber - 2) % 12 + 1
  const reflectedHouseNumber = ((2 * houseNumber - 2) % 12) + 1;
  const reflectedHouse = chartData.houses.find(h => h.houseNumber === reflectedHouseNumber) || chartData.houses[0];
  const reflectedLord = reflectedHouse.signLord as PlanetName;
  const reflectedLordPos = chartData.planets.find(p => p.name === reflectedLord);
  const reflectedLordSv = mapToSaptaVargaja(reflectedLordPos?.dignity || 'Neutral', reflectedLordPos?.isRetrograde || false);

  let reflectedStrength: BhavatBhavamEvaluation['reflectedHouseStrength'] = 'Neutral & Steady';
  if (reflectedLordSv.points >= 30 && ![6, 8, 12].includes(reflectedLordPos?.house || 1)) {
    reflectedStrength = 'Affirmative & Fortifying';
  } else if (reflectedLordSv.points <= 10 || ([6, 8, 12].includes(reflectedLordPos?.house || 1) && ![6, 8, 12].includes(reflectedHouseNumber))) {
    reflectedStrength = 'Compromised / Under Strain';
  }

  const bhavatBhavamNote = `Bhavat Bhavam (House ${formatOrdinal(reflectedHouseNumber)}, the ${formatOrdinal(houseNumber)} from ${formatOrdinal(houseNumber)}) provides the secondary energetic foundation. With reflected lord ${reflectedLord} in House ${formatOrdinal(reflectedLordPos?.house || 1)} (${reflectedLordSv.dignity}), the secondary root ${reflectedStrength === 'Affirmative & Fortifying' ? 'strongly validates and amplifies' : reflectedStrength === 'Neutral & Steady' ? 'steadily anchors' : 'sensitively challenges'} the primary house significations.`;

  // 5. Argala & Virodhargala Analysis
  // 2nd Argala balanced by 12th; 4th Argala balanced by 10th; 11th Argala balanced by 3rd; 5th Argala balanced by 9th.
  const argalaConfigs: Array<{
    offset: number;
    virodhaOffset: number;
    type: ArgalaIntervention['argalaType'];
  }> = [
    { offset: 1, virodhaOffset: 11, type: 'Primary Dhanargala (2nd)' },
    { offset: 3, virodhaOffset: 9, type: 'Sukha Argala (4th)' },
    { offset: 10, virodhaOffset: 2, type: 'Labhargala (11th)' },
    { offset: 4, virodhaOffset: 8, type: 'Secondary Sutargala (5th)' }
  ];

  const argalaMatrix: ArgalaIntervention[] = argalaConfigs.map(cfg => {
    const targetH = ((houseNumber + cfg.offset - 1) % 12) + 1;
    const virodhaH = ((houseNumber + cfg.virodhaOffset - 1) % 12) + 1;

    const planetsInArgala = chartData.planets.filter(p => p.house === targetH && p.name !== 'Ascendant').map(p => p.name);
    const planetsInVirodha = chartData.planets.filter(p => p.house === virodhaH && p.name !== 'Ascendant').map(p => p.name);

    const hasBeneficInArgala = planetsInArgala.some(p => NATURAL_BENEFICS.includes(p));
    const isObstructed = planetsInVirodha.length >= planetsInArgala.length && planetsInArgala.length > 0;

    let verdict: ArgalaIntervention['netInterventionVerdict'] = 'No Intervention';
    if (planetsInArgala.length > 0) {
      if (hasBeneficInArgala) {
        verdict = isObstructed ? 'Obstructed Benefic Support' : 'Unobstructed Benefic Support';
      } else {
        verdict = isObstructed ? 'Obstructed Malefic Tension' : 'Unobstructed Malefic Pressure';
      }
    }

    return {
      houseNumber: targetH,
      argalaType: cfg.type,
      planetsCausing: planetsInArgala,
      isBeneficArgala: hasBeneficInArgala,
      opposingVirodhaHouse: virodhaH,
      obstructingPlanets: planetsInVirodha,
      isArgalaObstructed: isObstructed,
      netInterventionVerdict: verdict
    };
  });

  // 6. Varga / Navamsha & Micro Confirmations
  const d9Chart = kundali.charts.D9;
  const d9House = d9Chart?.houses?.find(h => h.houseNumber === houseNumber);
  const d9Sign = d9House?.sign || houseSign;
  const d9Lord = (d9House?.signLord || ZODIAC_LORDS[d9Sign]) as PlanetName;

  const isVargottama = d9Sign === houseSign;
  const isPushkar = lordPos ? isPushkarNavamsha(lordPos.sign, lordPos.degreeInSign) : false;
  const isPushkarExact = lordPos ? isPushkarBhaga(lordPos.sign, lordPos.degreeInSign) : false;

  const savPoints = kundali.ashtakvarga ? getSavByBhava(kundali.ashtakvarga, houseNumber, kundali.houses) : 28;
  const bavEvaluation: VargaNavamshaBacking['bavEvaluation'] = 
    savPoints >= 30 ? 'Abundant (≥30 Bindus)' : savPoints >= 28 ? 'Average (28-29 Bindus)' : 'Deficient (<28 Bindus)';

  let d9DignityDesc = 'Friendly/Neutral Navamsha Backing';
  if (isVargottama) d9DignityDesc = 'Vargottama (Same in D1 & D9 - High Stability)';
  else if (isPushkar) d9DignityDesc = 'Pushkar Navamsha (Regenerative Cosmic Grace)';

  // Composite Score Calculation
  const compositeScore = Math.round(
    (vesselScore * 0.35) + 
    (lordScore * 0.40) + 
    (karakaScore * 0.25) + 
    ((savPoints - 28) * 1.5) +
    (isVargottama ? 6 : 0) +
    (isPushkar ? 8 : 0)
  );

  const clampedComposite = Math.max(15, Math.min(99, compositeScore));

  let netVitality: BhavaVitalityRating = 'Moderate (Madhyama)';
  if (clampedComposite >= 82) netVitality = 'Exemplary (Purna)';
  else if (clampedComposite >= 68) netVitality = 'Strong (Bala)';
  else if (clampedComposite >= 48) netVitality = 'Moderate (Madhyama)';
  else if (clampedComposite >= 35) netVitality = 'Mixed (Mishra)';
  else netVitality = 'Afflicted (Peedita)';

  // House Meta & Themes
  const houseTitles: Record<number, { title: string; sName: string; themes: string[]; cat: BhavaVesselCondition['houseCategory'] }> = {
    1: { title: 'Tanu Bhava (Self & Vitality)', sName: 'Tanu Bhava', themes: ['Physical Body', 'General Vitality', 'Personality', 'Head', 'Self-Realization'], cat: 'Kendra' },
    2: { title: 'Dhana Bhava (Wealth & Family)', sName: 'Dhana Bhava', themes: ['Accumulated Wealth', 'Speech', 'Family Lineage', 'Diet', 'Face & Eyes'], cat: 'Maraka' },
    3: { title: 'Sahaja Bhava (Courage & Siblings)', sName: 'Sahaja Bhava', themes: ['Initiative', 'Younger Siblings', 'Hands & Shoulders', 'Short Travels', 'Courage'], cat: 'Upachaya' },
    4: { title: 'Sukha Bhava (Mother & Happiness)', sName: 'Sukha Bhava', themes: ['Mother', 'Inner Peace', 'Vehicles & Property', 'Chest & Heart', 'Home'], cat: 'Kendra' },
    5: { title: 'Putra Bhava (Intellect & Progeny)', sName: 'Putra Bhava', themes: ['Higher Intellect', 'Children', 'Purva Punya', 'Speculation', 'Mantras'], cat: 'Trikona' },
    6: { title: 'Shatru Bhava (Debts & Health)', sName: 'Ari / Shatru Bhava', themes: ['Daily Service', 'Overcoming Obstacles', 'Immunity', 'Litigation', 'Debts'], cat: 'Dusthana' },
    7: { title: 'Kalatra Bhava (Partnership & Marriage)', sName: 'Jaya / Kalatra Bhava', themes: ['Spouse', 'Business Partnerships', 'Public Interactions', 'Trade', 'Foreign Residence'], cat: 'Kendra' },
    8: { title: 'Randhra Bhava (Longevity & Mystery)', sName: 'Randhra / Ayur Bhava', themes: ['Longevity', 'Occult & Research', 'Transformation', 'Inheritance', 'Unearned Wealth'], cat: 'Dusthana' },
    9: { title: 'Dharma Bhava (Fortune & Higher Wisdom)', sName: 'Dharma & Bhagya Bhava', themes: ['Higher Wisdom', 'Guru', 'Father & Ancestors', 'Long Pilgrimages', 'Divine Grace'], cat: 'Trikona' },
    10: { title: 'Karma Bhava (Career & Status)', sName: 'Karma & Rajya Bhava', themes: ['Profession', 'Public Status', 'Authority & Leadership', 'Father Karma', 'Worldly Impact'], cat: 'Kendra' },
    11: { title: 'Labha Bhava (Gains & Aspirations)', sName: 'Labha & Aya Bhava', themes: ['Gains & Revenue', 'Elder Siblings', 'Fulfillment of Desires', 'Social Networks', 'Awards'], cat: 'Upachaya' },
    12: { title: 'Vyaya Bhava (Moksha & Foreign Lands)', sName: 'Vyaya & Moksha Bhava', themes: ['Spiritual Liberation', 'Foreign Settlements', 'Subconscious Mind', 'Expenditure', 'Hospitality'], cat: 'Dusthana' }
  };

  const currentMeta = houseTitles[houseNumber] || houseTitles[1];

  // Strengths and Bottlenecks Synthesis
  const coreStrengths: string[] = [];
  const latentBottlenecks: string[] = [];

  if (lordSv.points >= 40) coreStrengths.push(`Bhavesha (${lordPlanet}) is highly fortified in ${lordSv.dignity}, ensuring strong executive stewardship over house affairs.`);
  else if (lordSv.points <= 10) latentBottlenecks.push(`Bhavesha (${lordPlanet}) is debilitated or in enemy sign, requiring deliberate strengthening and remediation.`);

  if (occupants.some(o => NATURAL_BENEFICS.includes(o.planet))) {
    coreStrengths.push(`Direct presence of natural benefics (${occupants.filter(o => NATURAL_BENEFICS.includes(o.planet)).map(o => o.planet).join(', ')}) enriches the house vessel with constructive growth.`);
  }
  if (occupants.some(o => NATURAL_MALEFICS.includes(o.planet) && ![3, 6, 11].includes(houseNumber))) {
    latentBottlenecks.push(`Natural malefic occupants (${occupants.filter(o => NATURAL_MALEFICS.includes(o.planet)).map(o => o.planet).join(', ')}) demand patient discipline and periodic stress management.`);
  }

  if (kartariStatus === 'Shubha Kartari (Flanked by Benefics)') {
    coreStrengths.push('Protected by Shubha Kartari Yoga (flanked by benefics), guarding house matters against external disruption.');
  } else if (kartariStatus === 'Papa Kartari (Hemmed by Malefics)') {
    latentBottlenecks.push('Constrained by Papa Kartari Yoga (hemmed by malefics), which can create periodic external bottlenecks or mental pressure.');
  }

  if (savPoints >= 30) coreStrengths.push(`High Ashtakavarga cushion (${savPoints} Bindus) provides buoyant manifestation ground.`);
  else if (savPoints < 25) latentBottlenecks.push(`Lower Ashtakavarga score (${savPoints} Bindus) means results materialize primarily through persistent conscious effort.`);

  if (isPushkar) coreStrengths.push(`Lord ${lordPlanet} is blessed in Pushkar Navamsha, granting regenerative recovery even through challenging transits.`);

  // Activation triggers
  const favorableTransits: string[] = [
    `Jupiter transiting over House ${houseNumber} or casting 5th/9th trine aspect onto ${houseSign}`,
    `Transiting House Lord (${lordPlanet}) traversing Kendra (1, 4, 7, 10) or Trikona (5, 9)`
  ];
  const vulnerableTransits: string[] = [
    `Saturn or Rahu transiting across House ${houseNumber} or the 8th from it without Jupiter aspect`,
    `Transiting Lord (${lordPlanet}) entering combust state or transiting 6th/8th/12th from natal position`
  ];

  const remedies: string[] = [
    `Strengthen Bhavesha (${lordPlanet}) through its classical deity stotra and mantra sadhana.`,
    `Harmonize Naisargika Karaka (${karakaPlanet}) to remove latent bottlenecks in ${currentMeta.themes[0]}.`,
    `Support Ashtakavarga bindu flow through targeted daana (charity) on the day ruled by ${lordPlanet}.`
  ];

  return {
    meta: {
      houseNumber,
      sanskritName: currentMeta.sName,
      englishTitle: currentMeta.title,
      coreSignifications: currentMeta.themes
    },
    headerSummary: {
      netVitality,
      compositeScorePct: clampedComposite,
      bavBinduCount: savPoints,
      houseArchetype: `${currentMeta.cat} • ${houseSign} (${houseSignSanskrit})`,
      executiveSummary: `House ${houseNumber} exhibits ${netVitality.toLowerCase()} vitality (${clampedComposite}% score). Evaluated through the Tri-Pillar rule with Lord ${lordPlanet} (${lordSv.dignity}), Karaka ${karakaPlanet}, and ${bavEvaluation.toLowerCase()}.`
    },
    triPillar: {
      vessel: {
        houseNumber,
        sign: houseSign,
        signSanskrit: houseSignSanskrit,
        houseCategory: currentMeta.cat,
        occupants,
        inboundDrishti,
        kartariStatus,
        flankingPlanets: {
          preceding12th: precPlanets,
          succeeding2nd: succPlanets
        },
        netVesselScore: vesselScore
      },
      manager: {
        lordPlanet,
        placedInHouse: lordPlacedHouse,
        placedInSign: lordPlacedSign,
        houseFromOwnSign: houseFromOwn,
        saptaVargaDignity: lordSv.dignity,
        dignityScorePoints: lordSv.points,
        dispositor: {
          planet: dispositorLordName,
          sign: lordPlacedSign,
          house: dispositorPos?.house || 1,
          dignity: dispositorSv.dignity,
          isStrong: isDispositorStrong
        },
        combustion: {
          isCombust: lordPos?.isCombust || false
        },
        retrogradation: {
          isVakri: lordPos?.isRetrograde || false
        },
        functionalRole: `Sign Lord of ${houseSign} (${formatOrdinal(houseNumber)} Bhava) residing in ${formatOrdinal(lordPlacedHouse)} House`,
        netLordScore: lordScore
      },
      deliverable: {
        naisargikaKaraka: karakaPlanet,
        placedInHouse: karakaPos?.house || 1,
        placedInSign: karakaPos?.sign || 'Aries',
        karakaDignity: karakaSv.dignity,
        karakoBhavanashaya,
        karakoBhavanashayaNote,
        karakaVitalityScore: karakaScore
      }
    },
    advancedVedicMetrics: {
      bhavatBhavam: {
        reflectedHouseNumber,
        reflectedHouseSign: reflectedHouse.sign,
        reflectedLord,
        reflectedLordHouse: reflectedLordPos?.house || 1,
        reflectedLordDignity: reflectedLordSv.dignity,
        reflectedHouseStrength: reflectedStrength,
        synthesisNote: bhavatBhavamNote
      },
      argalaMatrix,
      vargaConfirmations: {
        d9NavamshaSign: d9Sign,
        d9NavamshaLord: d9Lord,
        d9Dignity: d9DignityDesc,
        isPushkarNavamsha: isPushkar,
        isPushkarBhaga: isPushkarExact,
        isVargottama,
        ashtakavargaBAVPoints: savPoints,
        bavEvaluation
      }
    },
    synthesisAndActivation: {
      manifestationPromise: {
        coreStrengths: coreStrengths.length > 0 ? coreStrengths : [`Balanced manifestation through steady adherence to house themes.`],
        latentBottlenecks: latentBottlenecks.length > 0 ? latentBottlenecks : [`No severe afflictions detected; requires regular routine maintenance.`]
      },
      activationTriggers: {
        activeDashaCorrelation: `${lordPlanet} and ${karakaPlanet} Mahadasha/Antardasha sequences unlock primary bhava fruit.`,
        favorableTransits,
        vulnerableTransits
      },
      classicalRemedies: remedies
    }
  };
}
